// Phase 113 — Voice commands quick-chat menu. V opens menu, letter keys send line via chat.
import { useEffect, useState, type CSSProperties } from 'react';
import type { ClientChatMsg } from '../shared/protocol';

type VoiceLine = {
  key: string;
  label: string;
  line: string;
  team: 'team' | 'all';
};

const VOICE_LINES: VoiceLine[] = [
  { key: 'a', label: 'Acknowledged', line: 'Acknowledged.', team: 'team' },
  { key: 'm', label: 'Need a Medic', line: 'Need a medic!', team: 'team' },
  { key: 'f', label: 'Got our Flag', line: 'I have our flag!', team: 'team' },
  { key: 'e', label: 'Enemy in base', line: 'Enemy in our base!', team: 'team' },
  { key: 'b', label: 'Base under attack', line: 'Base under attack!', team: 'team' },
  { key: 't', label: 'Thanks', line: 'Thanks!', team: 'team' },
  { key: 'g', label: 'Going for flag', line: 'Going for the flag.', team: 'team' },
  { key: 'd', label: 'Defending', line: 'Defending base.', team: 'team' },
];

type VoiceCommandMenuProps = {
  sendMessage: (msg: unknown) => void;
};

export function VoiceCommandMenu({ sendMessage }: VoiceCommandMenuProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ignore if any input is focused
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        return;
      }
      if (!open) {
        if (e.key === 'v' || e.key === 'V') {
          e.preventDefault();
          setOpen(true);
        }
        return;
      }
      // Menu is open
      if (e.key === 'Escape' || e.key === 'v' || e.key === 'V') {
        e.preventDefault();
        setOpen(false);
        return;
      }
      const lower = e.key.toLowerCase();
      const match = VOICE_LINES.find((l) => l.key === lower);
      if (match) {
        e.preventDefault();
        const msg: ClientChatMsg = { type: 'chat', text: match.line, team: match.team };
        sendMessage(msg);
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, sendMessage]);

  if (!open) return null;

  const panelStyle: CSSProperties = {
    position: 'fixed',
    bottom: 200,
    left: 20,
    minWidth: 240,
    padding: '10px 12px',
    background: 'rgba(5,10,20,0.86)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    border: '1px solid rgba(80,180,255,0.4)',
    fontFamily: 'monospace',
    color: '#edf7ff',
    zIndex: 200,
    pointerEvents: 'auto',
  };

  return (
    <div style={panelStyle}>
      <div style={{
        fontSize: 10,
        letterSpacing: 2,
        textTransform: 'uppercase',
        color: '#62ddff',
        marginBottom: 6,
      }}>
        Voice Commands
      </div>
      <div style={{ display: 'grid', gap: 3 }}>
        {VOICE_LINES.map((line) => (
          <div key={line.key} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12 }}>
            <span style={{
              display: 'inline-block',
              width: 16,
              height: 16,
              lineHeight: '16px',
              textAlign: 'center',
              border: '1px solid rgba(120,200,255,0.4)',
              color: '#ffcc66',
              fontWeight: 700,
              fontSize: 10,
            }}>
              {line.key.toUpperCase()}
            </span>
            <span style={{ color: '#edf7ff' }}>{line.label}</span>
          </div>
        ))}
      </div>
      <div style={{
        marginTop: 6,
        fontSize: 9,
        letterSpacing: 1.4,
        textTransform: 'uppercase',
        color: 'rgba(231,244,252,0.45)',
      }}>
        Esc / V to cancel
      </div>
    </div>
  );
}
